import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Calculator, ArrowRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { US_STATES, API } from '../../constants';

import { t } from '@/i18n';
const SEVERITY_CLASS = {
  ok: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  warning: 'text-amber-700 bg-amber-50 border-amber-200',
  critical: 'text-red-700 bg-red-50 border-red-200',
  expired: 'text-red-800 bg-red-100 border-red-300',
  unknown: 'text-slate-600 bg-slate-50 border-slate-200',
};

/**
 * Compact deadline calculator. Two flavours:
 *  - General (no `presetState`): user picks state — used on the homepage.
 *  - State-locked (`presetState="FL"` etc.): state hidden, label shown
 *    in the title — used on per-state landing pages so the same widget
 *    is purpose-built for every page without duplicating UI.
 */
export const NoticeDeadlineWidget = ({ presetState = null, presetStateName = null, compact = false }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(presetState || '');
  const [firstFurnish, setFirstFurnish] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Re-sync if the parent swaps the preset (rare but safe).
  useEffect(() => {
    if (presetState) setState(presetState);
  }, [presetState]);

  const stateOptions = useMemo(() => US_STATES || [], []);
  const lockedStateLabel = useMemo(() => {
    if (!presetState) return null;
    if (presetStateName) return presetStateName;
    const found = stateOptions.find((s) => s.value === presetState);
    return found?.label || presetState;
  }, [presetState, presetStateName, stateOptions]);

  const handleCalculate = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!state) { setError('Pick a state'); return; }
    if (!firstFurnish) { setError('Enter your first day on the project'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/public/deadline-calculator/calculate`, {
        state,
        first_furnish_date: firstFurnish,
      });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not calculate deadline');
    } finally {
      setLoading(false);
    }
  };

  const goToFull = () => {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    if (firstFurnish) params.set('first', firstFurnish);
    navigate(`/notice-deadline-calculator?${params.toString()}`);
  };

  const urgent = result?.most_urgent;

  return (
    <Card
      className="border-2 border-[#F97316]/30 shadow-lg bg-white"
      data-testid="notice-deadline-widget"
    >
      <CardContent className={compact ? 'p-5' : 'p-6 lg:p-7'}>
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-md bg-[#F97316]/10 flex items-center justify-center shrink-0">
            <Calculator className="h-5 w-5 text-[#F97316]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#F97316] font-semibold">{t("Free Tool")}</p>
            <h3 className="text-lg font-bold text-[#1e3a5f] leading-tight">
              {lockedStateLabel
                ? `When is my ${lockedStateLabel} notice due?`
                : t("When is my notice due?")}
            </h3>
          </div>
        </div>

        <form
          onSubmit={handleCalculate}
          className={`grid grid-cols-1 ${presetState ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-3`}
        >
          {!presetState && (
            <div>
              <Label htmlFor="widget-state" className="text-xs">{t("Project State")}</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger id="widget-state" data-testid="widget-state-select">
                  <SelectValue placeholder={t("Select…")} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {stateOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.value} — {s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="widget-first" className="text-xs">{t("First Day on Job")}</Label>
            <Input
              id="widget-first"
              type="date"
              value={firstFurnish}
              onChange={(e) => setFirstFurnish(e.target.value)}
              data-testid="widget-first-furnish-input"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white"
              data-testid="widget-calculate-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              {t("Calculate")}
            </Button>
          </div>
        </form>

        {error && (
          <p className="text-sm text-red-600 mt-3" data-testid="widget-error">{error}</p>
        )}

        {urgent && (
          <div
            className={`mt-4 border rounded-md p-4 ${SEVERITY_CLASS[urgent.severity] || SEVERITY_CLASS.unknown}`}
            data-testid="widget-result"
          >
            <div className="flex items-start gap-3">
              {urgent.severity === 'expired' || urgent.severity === 'critical'
                ? <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" />
                : <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />}
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider opacity-80">{result.state} — {urgent.name}</p>
                <p className="text-2xl font-bold mt-0.5">
                  {urgent.deadline_date
                    ? format(parseISO(urgent.deadline_date), 'MMM d, yyyy')
                    : '—'}
                </p>
                <p className="text-sm font-medium mt-0.5">
                  {urgent.days_remaining == null
                    ? ''
                    : urgent.days_remaining < 0
                      ? `${Math.abs(urgent.days_remaining)} day(s) past deadline`
                      : `${urgent.days_remaining} day(s) remaining`}
                </p>
                <Button
                  size="sm"
                  variant="link"
                  onClick={goToFull}
                  className="px-0 h-auto text-[#1e3a5f] mt-2 hover:text-[#F97316]"
                  data-testid="widget-see-full-btn"
                >
                  {t("See all deadlines + statute references")}
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {!result && !loading && (
          <p className="text-xs text-slate-500 mt-3">
            {t("Free, no signup.")} {presetState ? `${lockedStateLabel} statute-accurate.` : t("We support all 50 states + DC.")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default NoticeDeadlineWidget;
